/**
 * 🎨 AFFILIATE WIDGETS - Widgets et Bannières pour Affiliés
 * 
 * Permet aux affiliés de générer des widgets/bannières à intégrer sur leur site
 * - Bannières de différentes tailles
 * - Code HTML à copier
 * - Preview en temps réel
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, ExternalLink, Image, Code, Palette, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

// 🎨 Thèmes de couleur disponibles
const THEMES = {
  pink: {
    name: 'Rose',
    gradient: 'from-pink-500 to-purple-600',
    bg: '#ec4899',
    text: '#ffffff'
  },
  purple: {
    name: 'Violet',
    gradient: 'from-purple-500 to-indigo-600',
    bg: '#8b5cf6',
    text: '#ffffff'
  },
  dark: {
    name: 'Sombre',
    gradient: 'from-gray-800 to-gray-900',
    bg: '#1f2937',
    text: '#ffffff'
  },
  light: {
    name: 'Clair',
    gradient: 'from-white to-gray-100',
    bg: '#ffffff',
    text: '#1f2937'
  }
};

// 📐 Tailles de bannières
const BANNER_SIZES = {
  leaderboard: { width: 728, height: 90, name: 'Leaderboard (728x90)' },
  rectangle: { width: 300, height: 250, name: 'Rectangle (300x250)' },
  skyscraper: { width: 160, height: 600, name: 'Skyscraper (160x600)' },
  mobile: { width: 320, height: 100, name: 'Mobile (320x100)' },
  square: { width: 250, height: 250, name: 'Carré (250x250)' },
  story: { width: 1080, height: 1920, name: 'Story (1080x1920)', scale: 0.15 }
};

/**
 * 🖼️ Composant Bannière Preview
 */
const BannerPreview = ({ size, theme, affiliateCode, style = 'modern' }) => {
  const sizeConfig = BANNER_SIZES[size];
  const themeConfig = THEMES[theme];
  const scale = sizeConfig.scale || Math.min(1, 300 / sizeConfig.width);
  
  const isVertical = sizeConfig.height > sizeConfig.width;
  const isSquare = Math.abs(sizeConfig.height - sizeConfig.width) < 50;
  
  return (
    <div 
      className="relative overflow-hidden rounded-xl shadow-2xl"
      style={{
        width: sizeConfig.width * scale,
        height: sizeConfig.height * scale,
        background: `linear-gradient(135deg, ${themeConfig.bg}, ${theme === 'light' ? '#f3f4f6' : '#4c1d95'})`,
      }}
    >
      {/* Contenu de la bannière */}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center"
           style={{ color: themeConfig.text }}>
        
        {/* Logo/Emoji */}
        <div className={`${isVertical ? 'text-4xl mb-3' : 'text-2xl mb-1'}`}>
          💜
        </div>
        
        {/* Titre */}
        <h3 className={`font-bold ${isVertical ? 'text-xl mb-2' : isSquare ? 'text-lg mb-1' : 'text-base'}`}>
          {isVertical ? 'Rejoins Heartbeats !' : 'Heartbeats'}
        </h3>
        
        {/* Sous-titre */}
        {(isVertical || isSquare) && (
          <p className={`opacity-90 ${isVertical ? 'text-sm mb-3' : 'text-xs mb-2'}`}>
            L'app de rencontre la plus cool
          </p>
        )}
        
        {/* CTA */}
        <div 
          className={`px-3 py-1 rounded-full font-semibold ${isVertical ? 'text-sm' : 'text-xs'}`}
          style={{ 
            background: theme === 'light' ? '#ec4899' : 'rgba(255,255,255,0.2)',
            color: theme === 'light' ? '#fff' : themeConfig.text
          }}
        >
          15 jours GRATUITS →
        </div>
        
        {/* Code affilié discret */}
        {isVertical && (
          <p className="absolute bottom-2 text-[8px] opacity-50">
            Code: {affiliateCode}
          </p>
        )}
      </div>
      
      {/* Effets décoratifs */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-2xl" />
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-pink-300/20 rounded-full blur-xl" />
    </div>
  );
};

/**
 * 📋 Générateur de code HTML
 */
const generateEmbedCode = (affiliateCode, size, theme) => {
  const sizeConfig = BANNER_SIZES[size];
  const themeConfig = THEMES[theme];
  const link = `https://heartbeats.fr/?ref=${affiliateCode}&utm_source=widget&utm_medium=banner&utm_campaign=${size}`;
  
  return `<!-- Heartbeats Affiliate Banner - ${sizeConfig.name} -->
<a href="${link}" target="_blank" rel="noopener" style="display:inline-block;text-decoration:none;">
  <div style="
    width:${sizeConfig.width}px;
    height:${sizeConfig.height}px;
    background:linear-gradient(135deg,${themeConfig.bg},${theme === 'light' ? '#f3f4f6' : '#4c1d95'});
    border-radius:12px;
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
    color:${themeConfig.text};
    font-family:system-ui,-apple-system,sans-serif;
    box-shadow:0 4px 20px rgba(0,0,0,0.2);
    position:relative;
    overflow:hidden;
  ">
    <span style="font-size:${sizeConfig.height > sizeConfig.width ? '32' : '20'}px;margin-bottom:8px;">💜</span>
    <span style="font-weight:bold;font-size:${sizeConfig.height > sizeConfig.width ? '18' : '14'}px;">Heartbeats</span>
    <span style="font-size:11px;opacity:0.9;margin:4px 0;">15 jours GRATUITS</span>
    <span style="
      background:${theme === 'light' ? '#ec4899' : 'rgba(255,255,255,0.2)'};
      color:${theme === 'light' ? '#fff' : themeConfig.text};
      padding:6px 16px;
      border-radius:20px;
      font-size:12px;
      font-weight:600;
    ">Rejoindre →</span>
  </div>
</a>`;
};

/**
 * 📱 Générateur de lien avec UTM
 */
const generateTrackingLink = (affiliateCode, source) => {
  return `https://heartbeats.fr/?ref=${affiliateCode}&utm_source=${source}&utm_medium=affiliate&utm_campaign=referral`;
};

/**
 * 🎨 Dashboard Widgets Affilié
 */
export default function AffiliateWidgets({ affiliateCode, onClose }) {
  const [selectedSize, setSelectedSize] = useState('rectangle');
  const [selectedTheme, setSelectedTheme] = useState('pink');
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('banners'); // banners, links, social

  const handleCopyCode = () => {
    const code = generateEmbedCode(affiliateCode, selectedSize, selectedTheme);
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Code HTML copié !');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = (source) => {
    const link = generateTrackingLink(affiliateCode, source);
    navigator.clipboard.writeText(link);
    toast.success(`Lien ${source} copié !`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 overflow-y-auto"
    >
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center">
                <Image className="w-5 h-5" />
              </div>
              Widgets & Bannières
            </h1>
            <p className="text-gray-400 mt-1">Intégrez Heartbeats sur votre site ou réseaux</p>
          </div>
          <Button variant="ghost" onClick={onClose} className="text-white">
            ✕
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'banners', icon: Image, label: 'Bannières' },
            { id: 'links', icon: ExternalLink, label: 'Liens trackés' },
            { id: 'social', icon: Palette, label: 'Réseaux sociaux' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                activeTab === tab.id 
                  ? 'bg-pink-500 text-white' 
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content: Bannières */}
        {activeTab === 'banners' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Options */}
            <div className="space-y-6">
              {/* Sélection taille */}
              <div>
                <label className="text-white font-medium mb-3 block">📐 Taille</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(BANNER_SIZES).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedSize(key)}
                      className={`p-3 rounded-xl text-left text-sm transition-colors ${
                        selectedSize === key
                          ? 'bg-pink-500 text-white'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      {config.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sélection thème */}
              <div>
                <label className="text-white font-medium mb-3 block">🎨 Thème</label>
                <div className="flex gap-2">
                  {Object.entries(THEMES).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedTheme(key)}
                      className={`flex-1 p-3 rounded-xl text-center text-sm transition-all ${
                        selectedTheme === key
                          ? 'ring-2 ring-pink-500 ring-offset-2 ring-offset-black'
                          : ''
                      }`}
                      style={{ background: config.bg, color: config.text }}
                    >
                      {config.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Code HTML */}
              <div>
                <label className="text-white font-medium mb-3 block flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Code HTML à copier
                </label>
                <div className="bg-gray-900 rounded-xl p-4 max-h-40 overflow-auto">
                  <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
                    {generateEmbedCode(affiliateCode, selectedSize, selectedTheme)}
                  </pre>
                </div>
                <Button
                  onClick={handleCopyCode}
                  className="w-full mt-3 bg-gradient-to-r from-pink-500 to-purple-500"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copié !' : 'Copier le code HTML'}
                </Button>
              </div>
            </div>

            {/* Preview */}
            <div>
              <label className="text-white font-medium mb-3 block">👁️ Aperçu</label>
              <div className="bg-gray-800/50 rounded-xl p-6 flex items-center justify-center min-h-[300px]">
                <BannerPreview
                  size={selectedSize}
                  theme={selectedTheme}
                  affiliateCode={affiliateCode}
                />
              </div>
              <p className="text-gray-500 text-xs mt-2 text-center">
                Taille réelle: {BANNER_SIZES[selectedSize].width}x{BANNER_SIZES[selectedSize].height}px
              </p>
            </div>
          </div>
        )}

        {/* Tab Content: Liens trackés */}
        {activeTab === 'links' && (
          <div className="space-y-4">
            <p className="text-gray-400 mb-6">
              Utilisez ces liens pour tracker d'où viennent vos conversions. Chaque source est suivie séparément.
            </p>
            
            {[
              { source: 'instagram', emoji: '📸', name: 'Instagram', desc: 'Bio ou stories' },
              { source: 'tiktok', emoji: '🎵', name: 'TikTok', desc: 'Bio ou vidéos' },
              { source: 'youtube', emoji: '▶️', name: 'YouTube', desc: 'Description vidéo' },
              { source: 'twitter', emoji: '🐦', name: 'Twitter/X', desc: 'Tweets ou bio' },
              { source: 'facebook', emoji: '👤', name: 'Facebook', desc: 'Posts ou bio' },
              { source: 'blog', emoji: '📝', name: 'Blog/Site', desc: 'Articles' },
              { source: 'email', emoji: '📧', name: 'Email', desc: 'Newsletter' },
              { source: 'whatsapp', emoji: '💬', name: 'WhatsApp', desc: 'Messages' }
            ].map(item => (
              <div 
                key={item.source}
                className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{item.emoji}</span>
                  <div>
                    <p className="text-white font-medium">{item.name}</p>
                    <p className="text-gray-500 text-sm">{item.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-pink-400 bg-pink-500/10 px-2 py-1 rounded hidden md:block">
                    ?ref={affiliateCode}&utm_source={item.source}
                  </code>
                  <Button
                    size="sm"
                    onClick={() => handleCopyLink(item.source)}
                    className="bg-white/10 hover:bg-white/20"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tab Content: Réseaux sociaux */}
        {activeTab === 'social' && (
          <div className="space-y-6">
            <p className="text-gray-400">
              Textes prêts à l'emploi pour vos publications. Copiez et adaptez !
            </p>

            {/* Templates de posts */}
            {[
              {
                title: '📸 Story Instagram',
                text: `🔥 J'ai trouvé LA meilleure app de rencontre !\n\n💜 Heartbeats c'est :\n• Des profils vérifiés\n• Une vraie communauté\n• Des rencontres authentiques\n\n👇 15 jours GRATUITS avec mon lien\nhttps://heartbeats.fr/?ref=${affiliateCode}`,
                color: 'from-pink-500 to-orange-500'
              },
              {
                title: '🎵 Bio TikTok',
                text: `💜 Heartbeats - L'app de rencontre pour les vrais\n🎁 15 jours offerts → heartbeats.fr/?ref=${affiliateCode}`,
                color: 'from-cyan-500 to-blue-500'
              },
              {
                title: '🐦 Tweet',
                text: `J'ai testé plein d'apps de rencontre et Heartbeats 💜 c'est vraiment différent. Des vrais profils, une vraie communauté.\n\n15 jours gratuits pour tester 👇\nhttps://heartbeats.fr/?ref=${affiliateCode}`,
                color: 'from-blue-400 to-blue-600'
              },
              {
                title: '💬 Message WhatsApp',
                text: `Hey ! 😊\n\nJe suis sur une nouvelle app de rencontre trop cool, Heartbeats 💜\n\nSi tu veux tester, tu as 15 jours gratuits avec mon lien :\nhttps://heartbeats.fr/?ref=${affiliateCode}\n\nDis-moi ce que t'en penses !`,
                color: 'from-green-500 to-green-600'
              }
            ].map((template, idx) => (
              <div key={idx} className="rounded-xl overflow-hidden">
                <div className={`bg-gradient-to-r ${template.color} px-4 py-2`}>
                  <h3 className="text-white font-bold">{template.title}</h3>
                </div>
                <div className="bg-white/5 p-4">
                  <pre className="text-gray-300 text-sm whitespace-pre-wrap font-sans">
                    {template.text}
                  </pre>
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(template.text);
                      toast.success('Texte copié !');
                    }}
                    className="mt-3 bg-white/10 hover:bg-white/20"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copier ce texte
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20">
          <p className="text-gray-400 text-sm text-center">
            💡 <strong className="text-white">Astuce :</strong> Les liens avec UTM vous permettent de voir 
            quelle source génère le plus de conversions dans votre dashboard.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
